import React, { useState, useEffect, useRef } from 'react';
import { 
  LayoutDashboard, 
  Heart, 
  Stethoscope, 
  Pill, 
  Calendar, 
  Download, 
  Settings,
  Shield,
  Users,
  Key,
  Globe,
  Brain,
  Target,
  FileText,
  Smartphone,
  BarChart3,
  Utensils,
  Leaf,
  UserCheck,
  Microscope,
  FlaskConical,
  Calculator,
  Palette,
  Info,
  Moon,
  Sun,
  MessageCircle,
  ShoppingCart,
  Award,
  X,
  Star,
  Video,
  BookOpen,
  Zap,
  TrendingUp,
  CheckCircle,
  Share2,
  ChevronDown,
  ChevronRight,
  Menu,
  Search,
  Activity
} from 'lucide-react';
import { ActiveView } from '../../App';
import { useBetaMode } from '../../providers/BetaModeProvider';

interface NavigationProps {
  activeView: ActiveView;
  onViewChange: (view: ActiveView) => void;
  userRole?: string;
  showMobileMenu: boolean;
  setShowMobileMenu: (show: boolean) => void;
  darkMode?: boolean;
  toggleDarkMode?: () => void;
}

interface NavigationGroup {
  id: string;
  label: string;
  icon: React.ComponentType<any>;
  items: NavigationItem[];
  priority?: boolean;
}

interface NavigationItem {
  id: ActiveView;
  label: string;
  icon: React.ComponentType<any>;
  featured?: boolean;
  beta?: boolean;
  priority?: boolean;
}

const getNavigationStructure = (userRole?: string): NavigationGroup[] => {
  // Base navigation groups
  const groups: NavigationGroup[] = [
    {
      id: 'overview',
      label: 'Overview',
      icon: LayoutDashboard,
      items: [
        { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard }
      ]
    },
    {
      id: 'health',
      label: 'Health Tracking',
      icon: Heart,
      items: [
        { id: 'conditions', label: 'Conditions', icon: Heart },
        { id: 'symptoms', label: 'Basic Symptoms', icon: Stethoscope },
        { id: 'advanced-symptoms', label: 'AI Symptom Tracker', icon: Target },
        { id: 'medications', label: 'Medications', icon: Pill },
        { id: 'appointments', label: 'Appointments', icon: Calendar }
      ]
    },
    {
      id: 'nutrition',
      label: 'Nutrition',
      icon: Utensils,
      items: [
        { id: 'enhanced-food-tracker', label: 'Food Tracker', icon: Calculator, featured: true },
        { id: 'meal-log', label: 'Meal Logger', icon: Utensils, featured: true },
        { id: 'meal-planner', label: 'Meal Planner', icon: Calendar, featured: true },
        { id: 'recipe-book', label: 'Recipe Book', icon: BookOpen, featured: true },
        { id: 'grocery-list', label: 'Grocery Lists', icon: ShoppingCart, featured: true },
        { id: 'meal-recommendations', label: 'AI Recommendations', icon: Brain, featured: true, beta: true },
        { id: 'food-tracking', label: 'Food & Nutrition', icon: Utensils },
        { id: 'holistic-treatments', label: 'Holistic Treatments', icon: Leaf }
      ]
    },
    {
      id: 'insights',
      label: 'Insights & AI',
      icon: Brain,
      items: [
        { id: 'ai-coach', label: 'AI Health Coach', icon: Brain, featured: true },
        { id: 'analytics', label: 'Advanced Analytics', icon: BarChart3 },
        { id: 'ai-docs', label: 'Document Analysis', icon: FileText },
        { id: 'predictive-insights', label: 'Predictive Insights', icon: TrendingUp, beta: true }
      ]
    },
    {
      id: 'social',
      label: 'Guru Connect',
      icon: MessageCircle,
      items: [
        { id: 'guru-connect', label: 'Guru Connect', icon: MessageCircle, featured: true, beta: true },
        { id: 'health-marketplace', label: 'Health Marketplace', icon: ShoppingCart, beta: true },
        { id: 'trust-score', label: 'Trust System', icon: Star, beta: true },
        { id: 'content-creation', label: 'Content Hub', icon: BookOpen, beta: true },
        { id: 'live-sessions', label: 'Live Sessions', icon: Video, beta: true }
      ]
    },
    {
      id: 'tools',
      label: 'Tools & Data',
      icon: Settings,
      items: [
        { id: 'devices', label: 'Device Integration', icon: Smartphone },
        { id: 'export', label: 'Export Data', icon: Download },
        { id: 'encryption', label: 'Encryption Manager', icon: Key },
        { id: 'p2p-docs', label: 'P2P Documents', icon: Globe },
        { id: 'innovations', label: 'AI & Innovation', icon: Brain },
        { id: 'pwa-features', label: 'Mobile & PWA', icon: Zap, beta: true },
        { id: 'data-sharing', label: 'Secure Data Sharing', icon: Share2, beta: true }
      ]
    },
    {
      id: 'system',
      label: 'System',
      icon: Settings,
      items: [
        { id: 'security', label: 'Security & Privacy', icon: Shield },
        { id: 'settings', label: 'Settings', icon: Settings },
        { id: 'system-status', label: 'System Status', icon: Activity },
        { id: 'design-preview', label: 'Design Preview', icon: Palette },
        { id: 'layout-options', label: 'Layout Options', icon: LayoutDashboard, beta: true },
        { id: 'about', label: 'About & Contributors', icon: Info },
        { id: 'changelog', label: "What's New", icon: FileText }
      ]
    }
  ];

  // Role-based modifications
  if (userRole === 'lab_technician') {
    return [
      {
        id: 'lab',
        label: 'Lab Dashboard',
        icon: Microscope,
        priority: true,
        items: [
          { id: 'lab-tech', label: 'Lab Dashboard', icon: Microscope, priority: true },
          { id: 'lab-analyzer', label: 'AI Lab Analyzer', icon: FlaskConical }
        ]
      },
      ...groups.filter(g => g.id !== 'social') // Remove social features for lab techs
    ];
  }

  if (userRole === 'healthcare_provider') {
    return [
      {
        id: 'provider',
        label: 'Provider Dashboard',
        icon: UserCheck,
        priority: true,
        items: [
          { id: 'provider-dashboard', label: 'Provider Dashboard', icon: UserCheck, priority: true }
        ]
      },
      ...groups.filter(g => g.id !== 'social') // Remove social features for providers
    ];
  }

  if (userRole === 'guru' || userRole === 'health_coach') {
    return [
      {
        id: 'guru',
        label: 'Guru Dashboard',
        icon: Award,
        priority: true,
        items: [
          { id: 'guru-dashboard', label: 'Guru Dashboard', icon: Award, priority: true },
          { id: 'guru-connect', label: 'Guru Connect', icon: MessageCircle, featured: true },
          { id: 'health-marketplace', label: 'Health Marketplace', icon: ShoppingCart },
          { id: 'trust-score', label: 'Trust Score & Verification', icon: Star, beta: true },
          { id: 'content-creation', label: 'Content Creation', icon: BookOpen, beta: true }
        ]
      },
      ...groups.filter(g => g.id !== 'social') // Replace social with guru-specific
    ];
  }

  if (userRole === 'super_admin') {
    return [
      {
        id: 'admin',
        label: 'Admin Panel',
        icon: Users,
        priority: true,
        items: [
          { id: 'admin', label: 'Admin Panel', icon: Users, priority: true }
        ]
      },
      ...groups
    ];
  }

  return groups;
};

export const Navigation: React.FC<NavigationProps> = ({ 
  activeView, 
  onViewChange, 
  userRole, 
  showMobileMenu,
  setShowMobileMenu,
  darkMode = false,
  toggleDarkMode
}) => {
  const navigationGroups = getNavigationStructure(userRole);
  const betaMode = useBetaMode();
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set(['overview']));
  const [searchQuery, setSearchQuery] = useState('');
  const [isCompact, setIsCompact] = useState(false);
  const navRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  // Handle screen size changes
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 1024) {
        setIsCompact(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Close mobile menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (navRef.current && !navRef.current.contains(event.target as Node)) {
        if (window.innerWidth < 1024) {
          setShowMobileMenu(false);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [setShowMobileMenu]);

  // Filter out beta features if beta mode is disabled
  const filteredGroups = navigationGroups.map(group => ({
    ...group,
    items: group.items.filter(item => {
      if ('beta' in item && item.beta && !betaMode) {
        return false;
      }
      return true;
    })
  })).filter(group => group.items.length > 0);

  // Filter by search query
  const searchFilteredGroups = filteredGroups.map(group => ({
    ...group,
    items: group.items.filter(item => 
      item.label.toLowerCase().includes(searchQuery.toLowerCase()) ||
      group.label.toLowerCase().includes(searchQuery.toLowerCase())
    )
  })).filter(group => group.items.length > 0);

  const toggleGroup = (groupId: string) => {
    setExpandedGroups(prev => {
      const newSet = new Set(prev);
      if (newSet.has(groupId)) {
        newSet.delete(groupId);
      } else {
        newSet.add(groupId);
      }
      return newSet;
    });
  };

  const handleViewChange = (view: ActiveView) => {
    try {
      onViewChange?.(view);
      // Close mobile menu after navigation
      if (window.innerWidth < 1024) {
        setShowMobileMenu(false);
      }
    } catch (error) {
      console.error('Navigation error:', error);
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchFilteredGroups.length > 0 && searchFilteredGroups[0].items.length > 0) {
      handleViewChange(searchFilteredGroups[0].items[0].id);
    }
  };

  return (
    <>
      {/* Mobile Overlay */}
      {showMobileMenu && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden" />
      )}

      {/* Navigation Sidebar */}
      <div 
        ref={navRef}
        className={`fixed inset-y-0 left-0 z-50 bg-white dark:bg-slate-900 border-r border-slate-200 dark:border-slate-700 transform transition-all duration-300 ease-in-out ${
          isCompact ? 'w-16' : 'w-64'
        } ${
          showMobileMenu ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
        }`}
      >
        {/* Mobile Header */}
        <div className="lg:hidden flex items-center justify-between p-4 border-b border-slate-200 dark:border-slate-700">
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 bg-gradient-to-br from-blue-600 to-purple-600 rounded-lg flex items-center justify-center">
              <Heart className="h-5 w-5 text-white" />
            </div>
            <h1 className="text-lg font-bold text-slate-900 dark:text-slate-100">
              ZippyHealth
            </h1>
          </div>
          <button
            onClick={() => setShowMobileMenu(false)}
            className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Desktop Header */}
        <div className="hidden lg:block p-6 border-b border-slate-200 dark:border-slate-700">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 bg-gradient-to-br from-blue-600 to-purple-600 rounded-lg flex items-center justify-center">
                <Heart className="h-5 w-5 text-white" />
              </div>
              {!isCompact && (
                <h1 className="text-lg font-bold text-slate-900 dark:text-slate-100">
                  ZippyHealth
                </h1>
              )}
            </div>
            <button
              onClick={() => setIsCompact(!isCompact)}
              className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
            >
              <Menu className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* Search Bar */}
        {!isCompact && (
          <div className="p-4 border-b border-slate-200 dark:border-slate-700">
            <form onSubmit={handleSearch} className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
              <input
                ref={searchRef}
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search features..."
                className="w-full pl-10 pr-4 py-2 text-sm border border-slate-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-200 transition-colors"
              />
            </form>
          </div>
        )}

        {/* Navigation Content - Scrollable */}
        <div className="flex-1 overflow-hidden">
          <nav className="h-full overflow-y-auto py-4 nav-scrollbar">
            <div className="space-y-2 px-4">
              {searchFilteredGroups.map((group) => {
                const isExpanded = expandedGroups.has(group.id);
                const hasActiveItem = group.items.some(item => item.id === activeView);
                
                return (
                  <div key={group.id} className="space-y-1">
                    {/* Group Header */}
                    <button
                      onClick={() => toggleGroup(group.id)}
                      className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                        hasActiveItem 
                          ? 'bg-blue-50 text-blue-700 dark:bg-blue-900/20 dark:text-blue-300' 
                          : 'text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800'
                      }`}
                    >
                      <div className="flex items-center space-x-2 min-w-0">
                        <group.icon className="h-4 w-4 flex-shrink-0" />
                        {!isCompact && (
                          <>
                            <span className="truncate">{group.label}</span>
                            {group.priority && (
                              <span className="px-1.5 py-0.5 text-xs bg-red-100 text-red-700 dark:bg-red-900/20 dark:text-red-300 rounded flex-shrink-0">
                                Priority
                              </span>
                            )}
                          </>
                        )}
                      </div>
                      {!isCompact && (
                        isExpanded ? (
                          <ChevronDown className="h-4 w-4 flex-shrink-0" />
                        ) : (
                          <ChevronRight className="h-4 w-4 flex-shrink-0" />
                        )
                      )}
                    </button>

                    {/* Group Items */}
                    {isExpanded && !isCompact && (
                      <div className="ml-6 space-y-1">
                        {group.items.map((item) => {
                          const isActive = activeView === item.id;
                          const Icon = item.icon;
                          
                          return (
                            <button
                              key={item.id}
                              onClick={() => handleViewChange(item.id)}
                              className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm transition-colors ${
                                isActive
                                  ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300'
                                  : 'text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800'
                              }`}
                            >
                              <div className="flex items-center space-x-2 min-w-0">
                                <Icon className="h-4 w-4 flex-shrink-0" />
                                <span className="truncate">{item.label}</span>
                                {item.featured && (
                                  <span className="px-1.5 py-0.5 text-xs bg-green-100 text-green-700 dark:bg-green-900/20 dark:text-green-300 rounded flex-shrink-0">
                                    Featured
                                  </span>
                                )}
                                {item.beta && (
                                  <span className="px-1.5 py-0.5 text-xs bg-yellow-100 text-yellow-700 dark:bg-yellow-900/20 dark:text-yellow-300 rounded flex-shrink-0">
                                    Beta
                                  </span>
                                )}
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </nav>
        </div>

        {/* Bottom Controls */}
        <div className="p-4 border-t border-slate-200 dark:border-slate-700">
          <div className="flex items-center justify-between">
            <button
              onClick={toggleDarkMode}
              className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
              aria-label={darkMode ? 'Switch to light mode' : 'Switch to dark mode'}
            >
              {darkMode ? (
                <Sun className="h-5 w-5 text-slate-600 dark:text-slate-400" />
              ) : (
                <Moon className="h-5 w-5 text-slate-600 dark:text-slate-400" />
              )}
            </button>
            {!isCompact && (
              <div className="text-xs text-slate-500 dark:text-slate-400">
                v1.2-beta
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}; 